import { useState, useEffect, useCallback } from 'react';

export type CookieCategory = 'essential' | 'functional' | 'analytics' | 'marketing';

export interface CookieConsent {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface CookieConsentState extends CookieConsent {
  hasConsented: boolean;
  showBanner: boolean;
}

const COOKIE_CONSENT_KEY = 'cookie-consent';
const COOKIE_CONSENT_VERSION = '1.0';

const defaultConsent: CookieConsent = {
  essential: true, // Always true, can't be disabled
  functional: false,
  analytics: false,
  marketing: false,
};

export const useCookieConsent = () => {
  const [consentState, setConsentState] = useState<CookieConsentState>({
    ...defaultConsent,
    hasConsented: false,
    showBanner: true,
  });

  // Load consent from localStorage on mount
  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        if (parsed.version === COOKIE_CONSENT_VERSION) {
          setConsentState(prev => ({
            ...parsed.consent,
            hasConsented: true,
            showBanner: false,
          }));
        }
      } catch (error) {
        console.error('Error parsing saved cookie consent:', error);
      }
    }
  }, []);

  const updateConsent = useCallback((newConsent: Partial<CookieConsent>) => {
    const updatedConsent = {
      ...consentState,
      ...newConsent,
      essential: true, // Essential cookies always enabled
    };

    setConsentState({
      ...updatedConsent,
      hasConsented: true,
      showBanner: false,
    });

    // Save to localStorage
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      version: COOKIE_CONSENT_VERSION,
      consent: updatedConsent,
      timestamp: new Date().toISOString(),
    }));
  }, [consentState]);

  const acceptAll = useCallback(() => {
    updateConsent({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    });
  }, [updateConsent]);

  const acceptEssential = useCallback(() => {
    updateConsent({
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    });
  }, [updateConsent]);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    setConsentState({
      ...defaultConsent,
      hasConsented: false,
      showBanner: true,
    });
  }, []);

  const hideBanner = useCallback(() => {
    setConsentState(prev => ({
      ...prev,
      showBanner: false,
    }));
  }, []);

  return {
    ...consentState,
    updateConsent,
    acceptAll,
    acceptEssential,
    resetConsent,
    hideBanner,
  };
};